import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import DeletedMembers from './pages/DeletedMembers';
import MemberDetail from './pages/MemberDetail';
import AddMember from './pages/AddMember';
import EditMember from './pages/EditMember';
import AddPayment from './pages/AddPayment';
import PaymentHistory from './pages/PaymentHistory';
import DailyCheckIns from './pages/DailyCheckIns';
import TabletSignIn from './pages/TabletSignIn/TabletSignIn';
import PrivateRoute from './components/PrivateRoute';
import PrintContract from './pages/PrintContract';
import TestPrintContract from './pages/TestPrintContract';
import SendEmail from './pages/SendEmail';
import Settings from './pages/Settings';
import TestLogin from './pages/TestLogin';
import ApiTest from './pages/ApiTest';
import Reports from './pages/Reports';
import EstateAddressOverview from './pages/EstateAddressOverview';
// import AdminUsers from './pages/AdminUsers';
// import RoleBasedRoute from './components/RoleBasedRoute';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f9fafb',
      paper: '#fafbfc',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
    },
    divider: '#e5e9f0',
    success: {
      main: '#38a169',
      light: '#68d391',
      dark: '#2f855a',
    },
    warning: {
      main: '#ed8936',
      light: '#fbb063',
      dark: '#c05621',
    },
    error: {
      main: '#e53e3e',
      light: '#fc8181',
      dark: '#c53030',
    },
    info: {
      main: '#3182ce',
      light: '#63b3ed',
      dark: '#2c5282',
    },
  },
  typography: {
    fontFamily: "'neueHaasGrotesk', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'",
    h1: {
      fontWeight: 600,
      fontSize: '2.125rem',
      lineHeight: 1.235,
      color: '#1a202c',
    },
    h2: {
      fontWeight: 600,   
      fontSize: '1.5rem',
      color: '#1a202c',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.334,
      color: '#1a202c',
    },
    h4: { 
      fontWeight: 600,
      fontSize: '2.125rem',
      lineHeight: 1.235,
      color: '#1a202c',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: '#1a202c',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#1a202c',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      color: '#1a202c',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
      color: '#4a5568',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
          border: '1px solid #e5e9f0',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderColor: '#1976d2',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
          border: '1px solid #e5e9f0',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          padding: '10px 24px',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
          },
        },
        outlined: {
          borderColor: '#1976d2',
          color: '#1976d2',
          '&:hover': {
            borderColor: '#1565c0',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(247, 250, 252, 0.5)',
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.MuiAlert-standardSuccess': {
            backgroundColor: 'rgba(56, 161, 105, 0.1)',
            border: '1px solid rgba(56, 161, 105, 0.3)',
          },
          '&.MuiAlert-standardError': {
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            border: '1px solid rgba(229, 62, 62, 0.3)',
          },
          '&.MuiAlert-standardWarning': {
            backgroundColor: 'rgba(237, 137, 54, 0.1)',
            border: '1px solid rgba(237, 137, 54, 0.3)',
          },
          '&.MuiAlert-standardInfo': {
            backgroundColor: 'rgba(49, 130, 206, 0.1)',
            border: '1px solid rgba(49, 130, 206, 0.3)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
          borderBottom: '1px solid #e5e9f0',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          color: '#1a202c',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #fafbfc 0%, #f7fafc 100%)',
          borderRight: '1px solid #e5e9f0',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SocketProvider>
            <Routes>
              {/* Public routes - must come before the catch-all route */}
              <Route path="/login" element={<Login />} />
              <Route path="/test-login" element={<TestLogin />} />
              <Route path="/api-test" element={<ApiTest />} />
              <Route path="/print-contract/:id" element={<PrintContract />} />
              <Route path="/tablet" element={<TabletSignIn />} />
              <Route path="/tablet-sign-in" element={<TabletSignIn />} />

              {/* Protected routes */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-checkins"
                element={
                  <PrivateRoute>
                    <Layout>
                      <DailyCheckIns />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Members />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members/deleted"
                element={
                  <PrivateRoute>
                    <Layout>
                      <DeletedMembers />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members/add"
                element={
                  <PrivateRoute>
                    <Layout>
                      <AddMember />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members/:id/edit"
                element={
                  <PrivateRoute>
                    <Layout>
                      <EditMember />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members/:id/print-contract"
                element={
                  <PrivateRoute>
                    <Layout>
                      <PrintContract />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/test-print-contract"
                element={
                  <PrivateRoute>
                    <Layout>
                      <PrintContract />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/test-print-page"
                element={
                  <PrivateRoute>
                    <Layout>
                      <TestPrintContract />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/members/:id"
                element={
                  <PrivateRoute>
                    <Layout>
                      <MemberDetail />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/estate-addresses/overview"
                element={
                  <PrivateRoute>
                    <Layout>
                      <EstateAddressOverview />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <PrivateRoute>
                    <Layout>
                      <PaymentHistory />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/add-payment"
                element={
                  <PrivateRoute>
                    <Layout>
                      <AddPayment />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/send-email"
                element={
                  <PrivateRoute>
                    <Layout>
                      <SendEmail />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Reports />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </PrivateRoute>
                }
              />
              {/*
              <Route
                path="/admin/users"
                element={
                  <RoleBasedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminUsers />
                    </Layout>
                  </RoleBasedRoute>
                }
              />
              */}
            </Routes>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
